import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense, type ReactNode } from "react";
import { useAuth } from "./auth";
import Layout from "./components/Layout";
import { Spinner } from "./components/ui";
import IdleLogout from "./components/IdleLogout";
import { StatusProvider } from "./lib/statuses";

// ページは遅延読み込み（コード分割）。初回に読むJSを小さくして表示を速くする。
// ルート単位で分割されるため、開いたページのぶんだけダウンロードされる。
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ExecDashboard = lazy(() => import("./pages/ExecDashboard"));
const Payroll = lazy(() => import("./pages/Payroll"));
const MyPayslip = lazy(() => import("./pages/MyPayslip"));
const CollabDeals = lazy(() => import("./pages/CollabDeals"));
const Deals = lazy(() => import("./pages/Deals"));
const DealDetail = lazy(() => import("./pages/DealDetail"));
const NewDeal = lazy(() => import("./pages/NewDeal"));
const Partners = lazy(() => import("./pages/Partners"));
const PartnerDetail = lazy(() => import("./pages/PartnerDetail"));
const Customers = lazy(() => import("./pages/Customers"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const Companies = lazy(() => import("./pages/Companies"));
const CompanyDetail = lazy(() => import("./pages/CompanyDetail"));
const Contracts = lazy(() => import("./pages/Contracts"));
const ContractDetail = lazy(() => import("./pages/ContractDetail"));
const SignContract = lazy(() => import("./pages/SignContract"));
const Vendors = lazy(() => import("./pages/Vendors"));
const Interviewees = lazy(() => import("./pages/Interviewees"));
const Storage = lazy(() => import("./pages/Storage"));
const Domains = lazy(() => import("./pages/Domains"));
const Subsidy = lazy(() => import("./pages/Subsidy"));
const VendorDetail = lazy(() => import("./pages/VendorDetail"));
const SalesDashboard = lazy(() => import("./pages/SalesDashboard"));
const Opportunities = lazy(() => import("./pages/Opportunities"));
const SalesReports = lazy(() => import("./pages/SalesReports"));
const SnsLeads = lazy(() => import("./pages/SnsLeads"));
const Expenses = lazy(() => import("./pages/Expenses"));
const SalesSettings = lazy(() => import("./pages/SalesSettings"));
const Rules = lazy(() => import("./pages/Rules"));
const Payments = lazy(() => import("./pages/Payments"));
const Approvals = lazy(() => import("./pages/Approvals"));
const Closings = lazy(() => import("./pages/Closings"));
const Tasks = lazy(() => import("./pages/Tasks"));
const FollowUps = lazy(() => import("./pages/FollowUps"));
const Audit = lazy(() => import("./pages/Audit"));
const Settings = lazy(() => import("./pages/Settings"));
const Users = lazy(() => import("./pages/Users"));
const Refunds = lazy(() => import("./pages/Refunds"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Import = lazy(() => import("./pages/Import"));
const DLeads = lazy(() => import("./pages/DLeads"));
const DLeadDetail = lazy(() => import("./pages/DLeadDetail"));
const Trash = lazy(() => import("./pages/Trash"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const NotificationBroadcast = lazy(() => import("./pages/NotificationBroadcast"));
const StorePayments = lazy(() => import("./pages/StorePayments"));
const Operations = lazy(() => import("./pages/Operations"));
const LocoreachAnalytics = lazy(() => import("./pages/LocoreachAnalytics"));

function Protected({ children }: { children: ReactNode }) {
  const { user, loading, hasRole } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  // 税理士アカウントは給与計算（支払通知書）のみ。それ以外のURL（経費精算含む）は給与計算へ誘導。
  if (hasRole("tax") && !/^\/payroll(\/|$)/.test(pathname)) {
    return <Navigate to="/payroll" replace />;
  }
  // レイアウトは残したまま、中身（ページ）だけ読み込み中スピナーにする。
  return <Layout><Suspense fallback={<div className="p-6"><Spinner /></div>}>{children}</Suspense></Layout>;
}

// ページ遷移のたびに先頭へスクロール。
// （商談など縦に長い一覧を開くと前ページのスクロール位置が残り「途中から」表示される問題を防ぐ）
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <StatusProvider>
      <ScrollToTop />
      <IdleLogout />
      <Suspense fallback={<Spinner />}>
      <Routes>
      <Route path="/login" element={<Login />} />
      {/* 電子契約の署名ページ（ログイン不要・トークン認証） */}
      <Route path="/sign/:token" element={<SignContract />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/exec" element={<Protected><ExecDashboard /></Protected>} />
      <Route path="/operations" element={<Protected><Operations /></Protected>} />
      <Route path="/payroll" element={<Protected><Payroll /></Protected>} />
      <Route path="/my-payslip" element={<Protected><MyPayslip /></Protected>} />
      <Route path="/deals" element={<Protected><Deals /></Protected>} />
      <Route path="/deals/new" element={<Protected><NewDeal /></Protected>} />
      <Route path="/deals/:id" element={<Protected><DealDetail /></Protected>} />
      {/* 旧「補助金案件」は「案件（種別=補助金）」に統合 */}
      <Route path="/subsidy" element={<Protected><Subsidy /></Protected>} />
      <Route path="/subsidy/:id" element={<Protected><Subsidy /></Protected>} />
      <Route path="/subsidy-cases" element={<Navigate to="/subsidy" replace />} />
      <Route path="/subsidy-cases/:id" element={<Navigate to="/deals" replace />} />
      <Route path="/d-leads" element={<Protected><DLeads /></Protected>} />
      <Route path="/d-leads/:id" element={<Protected><DLeadDetail /></Protected>} />
      <Route path="/partners" element={<Protected><Partners /></Protected>} />
      <Route path="/partners/:id" element={<Protected><PartnerDetail /></Protected>} />
      <Route path="/customers" element={<Protected><Customers /></Protected>} />
      <Route path="/customers/:id" element={<Protected><CustomerDetail /></Protected>} />
      <Route path="/companies" element={<Protected><Companies /></Protected>} />
      <Route path="/companies/:id" element={<Protected><CompanyDetail /></Protected>} />
      <Route path="/contracts" element={<Protected><Contracts /></Protected>} />
      <Route path="/contracts/:id" element={<Protected><ContractDetail /></Protected>} />
      <Route path="/import" element={<Protected><Import /></Protected>} />
      <Route path="/vendors" element={<Protected><Vendors /></Protected>} />
      <Route path="/vendors/:id" element={<Protected><VendorDetail /></Protected>} />
      <Route path="/interviewees" element={<Protected><Interviewees /></Protected>} />
      <Route path="/storage" element={<Protected><Storage /></Protected>} />
      <Route path="/domains" element={<Protected><Domains /></Protected>} />
      <Route path="/sales" element={<Protected><SalesDashboard /></Protected>} />
      <Route path="/opportunities" element={<Protected><Opportunities /></Protected>} />
      <Route path="/collab" element={<Protected><CollabDeals /></Protected>} />
      <Route path="/sales-reports" element={<Protected><SalesReports /></Protected>} />
      <Route path="/sns-leads" element={<Protected><SnsLeads /></Protected>} />
      <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
      <Route path="/sales-settings" element={<Protected><SalesSettings /></Protected>} />
      <Route path="/rules" element={<Protected><Rules /></Protected>} />
      <Route path="/payments" element={<Protected><Payments /></Protected>} />
      <Route path="/store-payments" element={<Protected><StorePayments /></Protected>} />
      <Route path="/locoreach-analytics" element={<Protected><LocoreachAnalytics /></Protected>} />
      <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
      <Route path="/closings" element={<Protected><Closings /></Protected>} />
      <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
      <Route path="/follow-ups" element={<Protected><FollowUps /></Protected>} />
      <Route path="/refunds" element={<Protected><Refunds /></Protected>} />
      <Route path="/audit" element={<Protected><Audit /></Protected>} />
      <Route path="/trash" element={<Protected><Trash /></Protected>} />
      <Route path="/users" element={<Protected><Users /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/notification-settings" element={<Protected><NotificationSettings /></Protected>} />
      <Route path="/notifications/send" element={<Protected><NotificationBroadcast /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </StatusProvider>
  );
}
